/**
 * Google OAuth Callback Component
 * Handles the redirect from Google OAuth and sends data to parent window
 */
import React, { useEffect } from 'react';
import { useLocation } from 'react-router';
import Loading from 'components/common/Loading';
import getSubdomain from 'utils/getSubdomain';
import { resolveOAuthCallbackTargetOrigin } from 'utils/webOrigin';
import { I18n } from 'utils/i18n';

const GoogleOAuthCallback = () => {
  const location = useLocation();
  const [status, setStatus] = React.useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = React.useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const success = urlParams.get('success');
    const data = urlParams.get('data');
    const error = urlParams.get('error');
    const targetOrigin = resolveOAuthCallbackTargetOrigin(
      getSubdomain(),
      urlParams.get('subDomain'),
    );

    if (success === 'true' && data) {
      try {
        const authData = JSON.parse(data);

        // Send success message to parent window
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'GOOGLE_OAUTH_SUCCESS',
              ...authData,
            },
            targetOrigin,
          );
        }

        setStatus('success');
        setMessage(
          I18n.t('OAuthCallback.google.connected_as', { email: authData.userInfo?.email }) ||
            I18n.t('OAuthCallback.google.connected_as_default'),
        );

        // Auto-close after 3 seconds
        setTimeout(() => {
          window.close();
        }, 3000);
      } catch (parseError) {
        console.error('Failed to parse OAuth data:', parseError);

        // Send error message to parent window
        if (window.opener) {
          window.opener.postMessage(
            {
              type: 'GOOGLE_OAUTH_ERROR',
              error: 'Failed to parse authentication data',
            },
            targetOrigin,
          );
        }

        setStatus('error');
        setMessage(I18n.t('OAuthCallback.google.parse_error'));
      }
    } else if (success === 'false' && error) {
      // Send error message to parent window
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'GOOGLE_OAUTH_ERROR',
            error,
          },
          targetOrigin,
        );
      }

      setStatus('error');
      setMessage(error);
    } else {
      // Invalid callback parameters
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'GOOGLE_OAUTH_ERROR',
            error: 'Invalid OAuth callback parameters',
          },
          targetOrigin,
        );
      }

      setStatus('error');
      setMessage(I18n.t('OAuthCallback.google.invalid_params'));
    }
  }, [location]);

  if (status === 'processing') {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
        }}
      >
        <Loading />
        <p style={{ marginTop: '20px', color: '#666' }}>
          {I18n.t('OAuthCallback.completing_auth')}
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        fontFamily: 'Arial, sans-serif',
        backgroundColor: '#f5f5f5',
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
        }}
      >
        <div style={{ marginBottom: '20px', fontSize: '48px' }}>
          {status === 'success' ? '✅' : '❌'}
        </div>
        <h2 style={{ color: '#333', marginBottom: '16px' }}>
          {status === 'success'
            ? I18n.t('OAuthCallback.auth_complete')
            : I18n.t('OAuthCallback.auth_failed')}
        </h2>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          {status === 'success'
            ? I18n.t('OAuthCallback.google.success_message')
            : I18n.t('OAuthCallback.google.error_message')}
        </p>
        {message && (
          <p
            style={{
              color: status === 'success' ? '#4caf50' : '#f44336',
              fontSize: '14px',
              marginBottom: '20px',
            }}
          >
            {message}
          </p>
        )}
        <p style={{ color: '#888', fontSize: '14px' }}>
          {status === 'success'
            ? I18n.t('OAuthCallback.auto_close_message')
            : I18n.t('OAuthCallback.error_close_message')}
        </p>
        <button
          onClick={() => window.close()}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: status === 'success' ? '#4285f4' : '#666',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          {I18n.t('OAuthCallback.close_tab')}
        </button>
      </div>
    </div>
  );
};

export default GoogleOAuthCallback;
